import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Department, DEPARTMENT_COLORS } from '../types/department';
import { useDarkMode, DM } from '../context/DarkModeContext';
import DashboardHeader from '../components/DashboardHeader';

const DepartmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useDarkMode();
  const d = darkMode;

  const department = location.state?.department;

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  const colors = DEPARTMENT_COLORS[department?.accentColor];

  const filteredMembers = useMemo(() => {
    let result = (department?.members || []).filter((member) =>
      member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.empId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (sortBy === 'entries') {
      result.sort((a, b) => b.totalEntries - a.totalEntries);
    } else {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [department, searchTerm, sortBy]);
  if (!department) {
    return (
      <div style={{ minHeight: '100vh', background: DM.pageBg(d) }}>
        <DashboardHeader label="Department Details">
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: 'rgba(255,255,255,0.15)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '10px',
              padding: '8px 16px',
              fontWeight: '600',
              fontSize: '15px',
              cursor: 'pointer',
            }}
          >
            ← Back
          </button>
        </DashboardHeader>
        <div style={{ padding: '40px 32px', textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: '42px', marginBottom: '12px' }}>⚠️</div>
          <p>No data. Please select a department from the dashboard.</p>
          <button
            onClick={() => navigate('/admin')}
            style={{
              marginTop: '20px',
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '15px',
            }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }



  return (
    <div style={{ minHeight: '100vh', background: DM.pageBg(d), fontFamily: "'Nunito', sans-serif" }}>
      <DashboardHeader label={`${department.name} Faculty`}>
        <button
          onClick={() => navigate('/admin')}
          style={{
            background: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '10px',
            padding: '8px 16px',
            fontWeight: '600',
            fontSize: '15px',
            cursor: 'pointer',
          }}
        >
          ← Back to Dashboard
        </button>
      </DashboardHeader>

      <div style={{ padding: '24px 32px' }}>
        {/* Department Info Card */}
        <div
          style={{
            background: colors[50],
            border: `1px solid ${colors[200]}`,
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
          }}
        >
          <div>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '700',
                color: colors[600],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              Department Name
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: colors[800] }}>
              {department.name}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '700',
                color: colors[600],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              Faculty Count
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: colors[800] }}>
              {department.facultyCount}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '700',
                color: colors[600],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              Total Contributions
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: colors[800] }}>
              {department.totalEntries}
            </div>
          </div>
          <div>
            <div
              style={{
                fontSize: '12px',
                fontWeight: '700',
                color: colors[600],
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: '4px',
              }}
            >
              Average per Faculty
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: colors[800] }}>
              {department.facultyCount > 0
                ? Math.round((department.totalEntries / department.facultyCount) * 10) / 10
                : 0}
            </div>
          </div>
        </div>

        {/* Search and Sort */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '24px',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: '250px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: d ? '#1e293b' : '#f8fafc',
              border: `1.5px solid ${d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              borderRadius: '10px',
              padding: '8px 16px',
            }}
          >
            <span style={{ fontSize: '16px' }}>🔍</span>
            <input
              type="text"
              placeholder="Search by name, emp ID, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontSize: '14px',
                color: d ? '#e2e8f0' : '#334155',
                width: '100%',
                fontWeight: '500',
              }}
            />
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              background: d ? '#1e293b' : '#f8fafc',
              border: `1.5px solid ${d ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
              borderRadius: '10px',
              padding: '8px 14px',
              fontSize: '14px',
              fontWeight: '700',
              cursor: 'pointer',
              color: d ? '#e2e8f0' : '#334155',
            }}
          >
            <option value="name">Sort by Name</option>
            <option value="entries">Sort by Contributions</option>
          </select>
        </div>

        {/* Faculty Table */}
        <div
          style={{
            background: d ? 'rgba(255,255,255,0.04)' : '#fff',
            borderRadius: '12px',
            border: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
            overflow: 'hidden',
            boxShadow: d
              ? '0 2px 8px rgba(0,0,0,0.2)'
              : '0 1px 3px rgba(15,37,87,0.06)',
          }}
        >
          {filteredMembers.length === 0 ? (
            <div
              style={{
                padding: '60px 24px',
                textAlign: 'center',
                color: d ? '#94a3b8' : '#94a3b8',
              }}
            >
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>👥</div>
              <p style={{ fontSize: '16px', fontWeight: '600' }}>No faculty members found</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: d ? 'rgba(255,255,255,0.04)' : '#f8fafc',
                      borderBottom: `1px solid ${d ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
                    }}
                  >
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '800',
                        color: d ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Faculty Name
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '800',
                        color: d ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Emp ID
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'left',
                        fontWeight: '800',
                        color: d ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '800',
                        color: d ? '#e2e8f0' : '#1e293b',
                      }}
                    >
                      Total Contributions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((member, idx) => (
                    <tr
                      key={member.id}
                      style={{
                        borderBottom: `1px solid ${d ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`,
                        background: idx % 2 === 0 ? 'transparent' : d ? 'rgba(255,255,255,0.02)' : '#f8fafc',
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = d
                          ? 'rgba(255,255,255,0.06)'
                          : '#eff6ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          idx % 2 === 0 ? 'transparent' : d ? 'rgba(255,255,255,0.02)' : '#f8fafc';
                      }}
                    >
                      <td style={{ padding: '12px 16px' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          }}
                        >
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: colors[100],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '13px',
                              fontWeight: '900',
                              color: colors[900],
                              flexShrink: 0,
                            }}
                          >
                            {member.name.charAt(0).toUpperCase()}
                          </div>
                          <div
                            style={{
                              color: d ? '#e2e8f0' : '#1e293b',
                              fontWeight: '700',
                            }}
                          >
                            {member.name}
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          color: d ? '#94a3b8' : '#64748b',
                        }}
                      >
                        {member.empId}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          color: d ? '#94a3b8' : '#64748b',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={member.email}
                      >
                        {member.email}
                      </td>
                      <td
                        style={{
                          padding: '12px 16px',
                          textAlign: 'center',
                          fontWeight: '800',
                          color: colors[600],
                          fontSize: '16px',
                        }}
                      >
                        {member.totalEntries}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '24px' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{
              background: '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '700',
              cursor: 'pointer',
              fontSize: '15px',
            }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentDetail;
